import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Plus, Check, Trash, Circle, CircleHalf, XCircle, Warning, Clock,
  ListChecks, User as UserIcon, Briefcase, Sparkle, CalendarBlank, ArrowsClockwise,
  TreeStructure,
} from "@phosphor-icons/react";

const HR_ROLES = new Set(["super_admin", "company_admin", "hr", "manager"]);

const CATEGORY_META = {
  personal: { label: "Personal", tone: "bg-zenith-teal/10 border-zenith-teal/30 text-zenith-teal-700" },
  hr: { label: "HR", tone: "bg-zenith-magenta/10 border-zenith-magenta/30 text-zenith-magenta" },
  project: { label: "Project", tone: "bg-zenith-blue-500/10 border-zenith-blue-500/30 text-zenith-blue-700" },
};

const STATUS_META = {
  todo: { label: "To do", icon: Circle, tone: "text-zinc-500" },
  in_progress: { label: "In progress", icon: CircleHalf, tone: "text-blue-600" },
  done: { label: "Done", icon: Check, tone: "text-emerald-600" },
  blocked: { label: "Blocked", icon: XCircle, tone: "text-red-600" },
};

const PRIORITY_META = {
  low: { label: "Low", tone: "bg-zinc-100 text-zinc-600 border-zinc-200" },
  medium: { label: "Medium", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  high: { label: "High", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  urgent: { label: "Urgent", tone: "bg-red-50 text-red-700 border-red-200" },
};

function dueLabel(due) {
  if (!due) return { text: "No due date", tone: "text-zinc-400" };
  const today = new Date().toISOString().slice(0, 10);
  if (due < today) return { text: `Overdue · ${due}`, tone: "text-red-600 font-semibold" };
  if (due === today) return { text: "Due today", tone: "text-amber-700 font-semibold" };
  return { text: `Due ${due}`, tone: "text-zinc-600" };
}

function TaskRow({ t, employees, projects, onUpdate, onDelete, currentUserId, currentUserRole }) {
  const StatusIcon = STATUS_META[t.status]?.icon || Circle;
  const catMeta = CATEGORY_META[t.category] || CATEGORY_META.personal;
  const prMeta = PRIORITY_META[t.priority] || PRIORITY_META.medium;
  const due = dueLabel(t.due_date);
  const isAssignee = t.assignee_id === currentUserId;
  const isCreator = t.created_by === currentUserId;
  const isAdmin = HR_ROLES.has(currentUserRole);
  const canUpdate = isAssignee || isCreator || isAdmin;
  const canDelete = isCreator || ["super_admin", "company_admin", "hr"].includes(currentUserRole);

  const assigneeName = employees[t.assignee_id]?.name || "—";
  const project = projects[t.project_id];

  const toggleDone = () => {
    onUpdate(t.id, { status: t.status === "done" ? "todo" : "done" });
  };

  return (
    <div data-testid={`task-row-${t.id}`} className={`border border-zinc-200 rounded-sm bg-white p-3 flex items-start gap-3 hover:border-adb-navy/30 transition-colors ${t.status === "done" ? "opacity-60" : ""}`}>
      <button
        onClick={canUpdate ? toggleDone : undefined}
        disabled={!canUpdate}
        data-testid={`task-toggle-${t.id}`}
        className={`shrink-0 mt-0.5 ${canUpdate ? "hover:scale-110" : "cursor-not-allowed"} transition-transform`}
        title={t.status === "done" ? "Mark as to-do" : "Mark as done"}
      >
        <StatusIcon size={18} weight={t.status === "done" ? "fill" : "regular"} className={STATUS_META[t.status]?.tone} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <div className={`text-sm font-medium truncate ${t.status === "done" ? "line-through text-zinc-400" : "text-adb-navy"}`}>{t.title}</div>
          <Badge variant="outline" className={`rounded-sm text-[10px] ${catMeta.tone}`}>{catMeta.label}</Badge>
          <Badge variant="outline" className={`rounded-sm text-[10px] ${prMeta.tone}`}>{prMeta.label}</Badge>
          {t.source_kind && t.source_kind !== "manual" && (
            <Badge variant="outline" className="rounded-sm text-[10px] bg-zinc-100 text-zinc-600 border-zinc-200">{t.source_kind}</Badge>
          )}
        </div>
        {t.description && <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{t.description}</div>}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-zinc-500 flex-wrap">
          <span className={due.tone + " flex items-center gap-1"}><CalendarBlank size={12} weight="duotone" /> {due.text}</span>
          <span className="flex items-center gap-1"><UserIcon size={12} weight="duotone" /> {assigneeName}</span>
          {project && <span className="flex items-center gap-1"><Briefcase size={12} weight="duotone" /> {project.name}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {canUpdate && (
          <Select value={t.status} onValueChange={(v) => onUpdate(t.id, { status: v })}>
            <SelectTrigger data-testid={`task-status-${t.id}`} className="h-7 w-28 rounded-sm text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {canDelete && (
          <button
            onClick={() => onDelete(t.id)}
            data-testid={`task-delete-${t.id}`}
            className="p-1.5 rounded-sm hover:bg-red-50 text-red-500"
            title="Delete task"
          >
            <Trash size={14} />
          </button>
        )}
      </div>
    </div>
  );
}


function TaskList({ items, employees, projects, onUpdate, onDelete, currentUserId, currentUserRole, emptyMessage }) {
  if (!items.length) {
    return (
      <div data-testid="task-empty" className="border border-dashed border-zinc-200 rounded-sm p-10 text-center bg-zinc-50">
        <ListChecks size={28} weight="duotone" className="mx-auto text-zinc-300" />
        <div className="mt-2 text-sm font-medium text-zinc-600">{emptyMessage}</div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((t) => (
        <TaskRow key={t.id} t={t} employees={employees} projects={projects}
          onUpdate={onUpdate} onDelete={onDelete}
          currentUserId={currentUserId} currentUserRole={currentUserRole}
        />
      ))}
    </div>
  );
}


function NewTaskDialog({ users, projects, isAdmin, empsWithoutLogin, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "personal", priority: "medium",
    due_date: "", assignee_id: "", project_id: "",
  });

  const reset = () => setForm({
    title: "", description: "", category: "personal", priority: "medium",
    due_date: "", assignee_id: "", project_id: "",
  });

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.category === "project" && !form.project_id) { toast.error("Pick a project for a project task"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        category: form.category,
        priority: form.priority,
        due_date: form.due_date || undefined,
        assignee_id: form.assignee_id || undefined,
        project_id: form.project_id || undefined,
      };
      await api.post("/tasks", payload);
      toast.success("Task created");
      reset(); setOpen(false); onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="task-new-btn" className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">
          <Plus size={14} className="mr-1" /> New task
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-zenith-magenta" /> Create new task
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title <span className="text-red-500">*</span></Label>
            <Input data-testid="task-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Ship v0.16 changelog" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea data-testid="task-form-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" placeholder="Optional context or acceptance criteria…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="task-form-category" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal (for me)</SelectItem>
                  {isAdmin && <SelectItem value="hr">HR (assign to someone)</SelectItem>}
                  <SelectItem value="project">Project</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-form-priority" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <Input data-testid="task-form-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm mt-1 font-mono-plex" />
            </div>
            {isAdmin && form.category !== "personal" && (
              <div>
                <Label className="text-xs">Assign to</Label>
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger data-testid="task-form-assignee" className="rounded-sm mt-1"><SelectValue placeholder="Pick a person with a login" /></SelectTrigger>
                  <SelectContent>
                    {users.length === 0 && (
                      <div className="px-2 py-4 text-xs text-zinc-500 text-center">
                        No employees have active logins yet.<br/>
                        Create user accounts in the Admin panel first.
                      </div>
                    )}
                    {users.filter(u => !!u.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
                {empsWithoutLogin > 0 && (
                  <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                    <Warning size={11} weight="fill" /> {empsWithoutLogin} employee{empsWithoutLogin > 1 ? "s" : ""} without a login are hidden — they can't receive tasks
                  </div>
                )}
              </div>
            )}
            {form.category === "project" && (
              <div className="col-span-2">
                <Label className="text-xs">Project <span className="text-red-500">*</span></Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="task-form-project" className="rounded-sm mt-1"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code || p.name} · {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); setOpen(false); }} className="rounded-sm" data-testid="task-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="task-form-submit" className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold">
            {busy ? "Creating…" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = HR_ROLES.has(user?.role);

  const [tasks, setTasks] = useState([]);
  const [assignedByMe, setAssignedByMe] = useState([]);
  const [matrixTeam, setMatrixTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [empsWithoutLogin, setEmpsWithoutLogin] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [mine, prj, matrix] = await Promise.all([
        api.get("/tasks/mine"),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/tasks/dotted-team").catch(() => ({ data: [] })),
      ]);
      setTasks(mine.data);
      setProjects(prj.data);
      setMatrixTeam(matrix.data || []);
      if (isAdmin) {
        const [assigned, emps] = await Promise.all([
          api.get("/tasks/assigned-by-me").catch(() => ({ data: [] })),
          api.get("/employees").catch(() => ({ data: [] })),
        ]);
        setAssignedByMe(assigned.data);
        setUsers(emps.data.map((e) => ({ id: e.user_id || e.id, name: `${e.first_name} ${e.last_name}`.trim(), role: e.role || "employee" })));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const employeeMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    if (user) m[user.id] = { id: user.id, name: user.name + " (you)", role: user.role };
    return m;
  }, [users, user]);

  const projectMap = useMemo(() => {
    const m = {}; projects.forEach((p) => { m[p.id] = p; }); return m;
  }, [projects]);

  const applyStatusFilter = (items) => statusFilter === "all" ? items : items.filter((t) => t.status === statusFilter);

  const myTasksByCategory = useMemo(() => ({
    personal: applyStatusFilter(tasks.filter((t) => t.category === "personal")),
    hr: applyStatusFilter(tasks.filter((t) => t.category === "hr")),
    project: applyStatusFilter(tasks.filter((t) => t.category === "project")),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tasks, statusFilter]);

  const openCount = tasks.filter((t) => t.status === "todo" || t.status === "in_progress").length;
  const overdue = tasks.filter((t) => {
    if (!t.due_date || t.status === "done") return false;
    return t.due_date < new Date().toISOString().slice(0, 10);
  }).length;
  const doneToday = tasks.filter((t) => t.completed_at?.startsWith(new Date().toISOString().slice(0, 10))).length;

  const doUpdate = async (id, patch) => {
    try {
      await api.patch(`/tasks/${id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Task deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-magenta font-bold">Task Manager</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Get things done.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Your personal to-dos, HR-assigned tasks (onboarding, exit, compliance) and project work — all in one place. Auto-populates from onboarding kits and exit checklists.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="task-refresh"><ArrowsClockwise size={14} className={busy ? "animate-spin mr-1" : "mr-1"} /> Refresh</Button>
          <NewTaskDialog users={users} projects={projects} isAdmin={isAdmin} empsWithoutLogin={empsWithoutLogin} onCreated={load} />
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Open" value={openCount} icon={Circle} tone="teal" />
        <StatCard label="Overdue" value={overdue} icon={Warning} tone="magenta" />
        <StatCard label="Done today" value={doneToday} icon={Check} tone="green" />
        <StatCard label="Total" value={tasks.length} icon={ListChecks} tone="blue" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs text-zinc-500 uppercase tracking-widest font-semibold">Filter status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="task-status-filter" className="w-40 rounded-sm h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="my-personal">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="my-personal" className="rounded-sm" data-testid="tab-personal">
            <UserIcon size={12} className="mr-1" /> My tasks
            {myTasksByCategory.personal.length > 0 && <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] rounded-sm">{myTasksByCategory.personal.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="my-hr" className="rounded-sm" data-testid="tab-hr">
            <ListChecks size={12} className="mr-1" /> Assigned to me
            {myTasksByCategory.hr.length > 0 && <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] rounded-sm">{myTasksByCategory.hr.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="my-project" className="rounded-sm" data-testid="tab-project">
            <Briefcase size={12} className="mr-1" /> Project
            {myTasksByCategory.project.length > 0 && <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] rounded-sm">{myTasksByCategory.project.length}</Badge>}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="oversight" className="rounded-sm" data-testid="tab-oversight">
              <Clock size={12} className="mr-1" /> Assigned by me
              {assignedByMe.length > 0 && <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] rounded-sm">{assignedByMe.length}</Badge>}
            </TabsTrigger>
          )}
          {matrixTeam.length > 0 && (
            <TabsTrigger value="matrix" className="rounded-sm" data-testid="tab-matrix">
              <TreeStructure size={12} className="mr-1" /> My matrix team
              <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] rounded-sm bg-zenith-magenta/10 border-zenith-magenta/40 text-zenith-magenta">{matrixTeam.length}</Badge>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-personal" className="mt-4">
          <TaskList items={myTasksByCategory.personal} employees={employeeMap} projects={projectMap}
            onUpdate={doUpdate} onDelete={doDelete} currentUserId={user?.id} currentUserRole={user?.role}
            emptyMessage="No personal tasks yet. Click 'New task' to add your first to-do."
          />
        </TabsContent>
        <TabsContent value="my-hr" className="mt-4">
          <TaskList items={myTasksByCategory.hr} employees={employeeMap} projects={projectMap}
            onUpdate={doUpdate} onDelete={doDelete} currentUserId={user?.id} currentUserRole={user?.role}
            emptyMessage="Nothing assigned to you. Onboarding + exit + compliance tasks show up here."
          />
        </TabsContent>
        <TabsContent value="my-project" className="mt-4">
          <TaskList items={myTasksByCategory.project} employees={employeeMap} projects={projectMap}
            onUpdate={doUpdate} onDelete={doDelete} currentUserId={user?.id} currentUserRole={user?.role}
            emptyMessage="No project tasks assigned to you."
          />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="oversight" className="mt-4">
            <TaskList items={applyStatusFilter(assignedByMe)} employees={employeeMap} projects={projectMap}
              onUpdate={doUpdate} onDelete={doDelete} currentUserId={user?.id} currentUserRole={user?.role}
              emptyMessage="You haven't assigned any tasks yet. Click 'New task' with a category of HR or Project to assign one."
            />
          </TabsContent>
        )}
        {matrixTeam.length > 0 && (
          <TabsContent value="matrix" className="mt-4">
            <div className="border-l-2 border-zenith-magenta/40 bg-zenith-magenta/[0.03] px-4 py-3 rounded-r-sm mb-3 flex items-start gap-2" data-testid="matrix-team-hint">
              <TreeStructure size={16} weight="duotone" className="text-zenith-magenta mt-0.5 shrink-0" />
              <div className="text-xs text-zinc-700">
                <span className="font-semibold text-adb-navy">Matrix reporting</span> — you can review, update or complete tasks for people whose dotted-line manager is you (possibly in another company of the same group).
                <span className="text-zinc-500"> Payroll and leave approvals still route through their solid-line manager.</span>
              </div>
            </div>
            <TaskList items={applyStatusFilter(matrixTeam)} employees={employeeMap} projects={projectMap}
              onUpdate={doUpdate} onDelete={doDelete} currentUserId={user?.id} currentUserRole={user?.role}
              emptyMessage="No matrix-team tasks right now. When your dotted reports have open work, it will show up here."
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}


const TONE_MAP = {
  teal: { bg: "bg-zenith-teal/10", border: "border-zenith-teal/30", icon: "text-zenith-teal-700" },
  magenta: { bg: "bg-zenith-magenta/10", border: "border-zenith-magenta/30", icon: "text-zenith-magenta" },
  green: { bg: "bg-zenith-green/10", border: "border-zenith-green/30", icon: "text-zenith-green" },
  blue: { bg: "bg-zenith-blue-500/10", border: "border-zenith-blue-500/30", icon: "text-zenith-blue-700" },
};

function StatCard({ label, value, icon: Icon, tone }) {
  const t = TONE_MAP[tone] || TONE_MAP.teal;
  return (
    <div className={`border ${t.border} ${t.bg} rounded-sm px-4 py-3 flex items-center gap-3`}>
      <div className={`w-9 h-9 grid place-items-center rounded-sm bg-white ${t.border} border`}>
        <Icon size={18} weight="duotone" className={t.icon} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
        <div className="font-display text-2xl font-bold text-adb-navy leading-none">{value}</div>
      </div>
    </div>
  );
}
